import Location from 'svg/Location';
import AddressSearchEnhanced from 'components/forms/AddressSearch/AddressSearchEnhanced';
import type { UseAddressSearchReturn } from 'components/forms/AddressSearch/useAddressSearch';
import classNames from 'styles/utils/classNames';

const DROPDOWN_CLASS =
  'absolute top-full z-20 rounded-[1.25rem] bg-color-bg-lightmode-primary px-ds-xs py-ds-md shadow-fab dark:bg-color-bg-darkmode-primary';
const OPTION_CLASS =
  'typography-product-body flex h-[2.625rem] min-w-0 cursor-pointer select-none items-center rounded-md px-ds-sm text-color-text-lightmode-placeholder hover:bg-color-bg-lightmode-secondary hover:text-color-text-lightmode-primary data-[headlessui-state~=active]:bg-color-bg-lightmode-secondary dark:text-color-text-darkmode-placeholder dark:hover:bg-color-bg-darkmode-secondary dark:hover:text-color-text-darkmode-primary';

interface Props {
  id: string;
  /** `pill` is Figma's "Search by location"; `field` is the standard text input. */
  variant: 'pill' | 'field';
  search: UseAddressSearchReturn;
  placeholder: string;
  onInputChange?: UseAddressSearchReturn['handleAutocompleteSuggestions'];
}

export default function AddressField({ id, variant, search, placeholder, onInputChange }: Props) {
  const combobox = (
    <AddressSearchEnhanced
      id={id}
      autoComplete="off"
      placeholder={placeholder}
      handleSubmit={search.handleSubmitAutocomplete}
      handleAutcompleteSuggestions={onInputChange ?? search.handleAutocompleteSuggestions}
      addressString={search.addressString}
      suggestedAddresses={search.suggestedAddresses}
      activeAutocompleteAddress={search.activeAutocompleteAddress}
      isLoadingAutocomplete={search.isLoadingAutocomplete}
      autocompleteInputLength={search.autocompleteInputLength}
      autocompleteMinLength={search.autocompleteMinLength}
      isGoogleMapsAvailable={!search.googleMapsError}
      optionClassName={OPTION_CLASS}
      dropdownClassName={classNames(
        DROPDOWN_CLASS,
        // The pill's dropdown spans the whole pill, not just the text input inside it.
        variant === 'pill' ? '-left-10 mt-ds-lg w-[calc(100%+2.75rem)]' : 'left-0 mt-ds-sm w-full',
      )}
      className={
        variant === 'pill'
          ? 'input-unstyled typography-product-body-highlight w-full bg-transparent p-0 text-color-text-lightmode-primary placeholder:text-color-text-lightmode-placeholder dark:text-color-text-darkmode-primary dark:placeholder:text-color-text-darkmode-placeholder'
          : 'input-form typography-product-body py-[0.5625rem] focus:!ring-1 focus:!ring-color-border-hover-lightmode dark:focus:!ring-color-border-hover-darkmode'
      }
    />
  );

  if (variant === 'field') return combobox;

  return (
    <div className="flex h-10 items-center gap-ds-sm rounded-full bg-color-bg-input-lightmode-primary pl-ds-md pr-ds-xs dark:bg-color-bg-input-darkmode-primary">
      <Location className="h-5 w-5 shrink-0 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary" />
      <div className="min-w-0 flex-1">{combobox}</div>
    </div>
  );
}
